import { TTokenRedemptionOptionSelectResponse } from '@daymade/api-contracts';
import { TokenRedemptionOptionType } from '@daymade/common-lib/dbEnums';
import MainLayout from 'components/layout/MainLayout';
import ALittleLostSection from 'components/token-town/a-little-lost-section';
import ImageGallery from 'components/token-town/draws/image-gallery/ImageGallery';
import Header from 'components/token-town/header/Header';
import BackLink from 'components/token-town/links/BackLink';
import PageGrid from 'components/token-town/page-grid/PageGrid';
import Page from 'components/token-town/page/Page';
import PurchaseTokensBanner from 'components/token-town/purchase-tokens-banner/PurchaseTokensBanner';
import PlantTreeSection from 'components/token-town/tree/plant-tree-section/PlantTreeSection';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

interface ITreeRedemptionPage {
  treeRedemptionDetails: TTokenRedemptionOptionSelectResponse | null;
}

const TreeRedemptionPage = observer(
  ({ treeRedemptionDetails }: ITreeRedemptionPage) => {
    const router = useRouter();

    const { displayToast } = useUIStore();

    const { currentUser, balances, updateCurrentUser } = useUserStore();

    const tokenBalance = balances?.tokenBalance ?? 0;

    useEffect(() => {
      if (router.isReady && !treeRedemptionDetails) {
        router.replace('/404');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady, treeRedemptionDetails]);

    if (!treeRedemptionDetails) {
      return null;
    }

    const handleRedemption = async (quantity: number) => {
      const resp = await tokenApiClient.auth.redeemWithToken({
        params: { id: treeRedemptionDetails.id },
        body: {
          TREE: {
            quantity,
          },
        },
      });

      switch (resp.status) {
        case HttpStatusCode.OK:
          await updateCurrentUser();
          router.push(`/token-town/confirmation?id=${resp.body.id}`);
          break;

        case HttpStatusCode.BAD_REQUEST:
          displayToast({
            title: resp.body.message,
            color: '#FF6600',
            timeout: 5000,
          });
          break;

        default:
          break;
      }
    };

    return (
      <MainLayout>
        <NextSeo
          title="Plant a tree | Token Town | Win Epic Prizes | DAYMADE"
          description="Plant a tree"
          openGraph={{
            title: 'Plant a tree | Token Town | Win Epic Prizes | DAYMADE',
            description: 'Plant a tree',
          }}
        />
        <Header />

        <Page>
          {currentUser && <PurchaseTokensBanner tokenCount={tokenBalance} />}

          <PageGrid splitEarly>
            <ImageGallery
              tokenCost={treeRedemptionDetails.price ?? 0}
              images={[treeRedemptionDetails.heroImage]}
            />

            {treeRedemptionDetails.title && (
              <PlantTreeSection
                title={treeRedemptionDetails.title}
                playerTokenCount={tokenBalance}
                treeCost={treeRedemptionDetails.price}
                handleRedemption={handleRedemption}
              />
            )}
          </PageGrid>

          <BackLink to="prizes" />
        </Page>
        <ALittleLostSection />
      </MainLayout>
    );
  },
);

export const getServerSideProps: GetServerSideProps<
  ITreeRedemptionPage
> = async (ctx) => {
  const id = Array.isArray(ctx.query.id) ? ctx.query.id[0] : ctx.query.id;

  let tokenRedemptionOption = null;

  const parsedId = Number(id);
  if (isNaN(parsedId)) {
    return {
      props: {
        treeRedemptionDetails: tokenRedemptionOption,
      },
    };
  }

  const resp = await tokenApiClient.public.getTokenRedemptionOption({
    params: {
      id: parsedId,
    },
  });

  switch (resp.status) {
    case HttpStatusCode.OK:
      if (resp.body.type !== TokenRedemptionOptionType.TREE) {
        return {
          props: {
            treeRedemptionDetails: tokenRedemptionOption,
          },
        };
      }

      tokenRedemptionOption = {
        ...resp.body,
      };

      break;
    case HttpStatusCode.NOT_FOUND:
      console.error(
        `Error fetching tree redemption details: ${resp.body.message}`,
      );
      return {
        props: {
          treeRedemptionDetails: tokenRedemptionOption,
        },
      };
  }

  ctx.res.setHeader(
    'Cache-Control',
    'public, max-age=3600, s-maxage=3600, stale-while-revalidate=59',
  );

  return {
    props: {
      treeRedemptionDetails: tokenRedemptionOption,
    },
  };
};

export default TreeRedemptionPage;
